import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'
import Header from '@/components/layout/Header'
import EventListItem from '@/components/events/EventListItem'
import SquadBuilder from '@/components/events/SquadBuilder'
import EventConfigBuilder from '@/components/events/EventConfigBuilder'
import EventDetailPanel from '@/components/events/EventDetailPanel'
import type { Event } from '@/components/events/types'
import { Button } from '@/components/ui/button'
import { RefreshCcw } from 'lucide-react'
import { useAuth, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

const SPECIAL_EVENT_CHANNEL_ID = '1496125935982743553'

export default function Events() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const canConfigure = isOperator(user)

  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('select'))
  const [viewMode, setViewMode] = useState<'detail' | 'squads'>('detail')

  // Clear the query param after consuming it
  useEffect(() => {
    if (searchParams.has('select')) {
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    const url = force ? '/api/events?refresh=true' : '/api/events'
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(url)
        if (res.ok) {
          const data: Event[] = await res.json()
          // Keep previous data if server returns empty transiently
          setEvents(prev => data.length > 0 || prev.length === 0 ? data : prev)
          setLoading(false)
          return
        }
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? `Error ${res.status}`)
        setLoading(false)
        return // HTTP error — no point retrying immediately
      } catch {
        if (attempt === 2) setError('Failed to reach the server.')
        else await new Promise(r => setTimeout(r, 800 * (attempt + 1)))
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchEvents() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectEvent = (id: string) => {
    setSelectedId(id)
    setViewMode('detail')
  }

  const staleEventRef = useRef<Event | null>(null)
  const _foundEvent = selectedId ? (events.find(e => e.id === selectedId) ?? null) : null
  if (_foundEvent) staleEventRef.current = _foundEvent
  const selectedEvent = _foundEvent ?? (selectedId ? staleEventRef.current : null)

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={T.events.title} />

      <div className="flex flex-1 overflow-hidden">
        {/* Sol: etkinlik listesi — Maçlar sekmesinin listesiyle aynı kabuk
            (cam kenar sütunu, saç telli bölüm başlığı, tam kanama satırlar).
            Telefonda etkinlik seçiliyken düşer. */}
        <aside className={`${selectedEvent ? 'hidden md:flex' : 'flex'} w-full flex-col overflow-hidden border-r border-white/10 bg-black/25 backdrop-blur-sm md:w-80 md:shrink-0`}>
          <div className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-2.5">
            <h2 className="flex flex-1 items-center gap-2.5 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
              <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
              {T.events.title}
              {!(loading && events.length === 0) && (
                <span className="tabular-nums text-muted-foreground">{events.length}</span>
              )}
            </h2>
            <button
              onClick={() => fetchEvents(true)}
              disabled={loading}
              title={T.events.refresh}
              aria-label={T.events.refresh}
              className="rounded-sm p-1.5 text-muted-foreground/60 transition-colors hover:bg-white/5 hover:text-foreground disabled:opacity-40"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {error && <p className="px-4 py-3 text-xs text-destructive">{error}</p>}
            {!loading && events.length === 0 && !error && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">{T.events.noEvents}</p>
            )}
            {loading && events.length === 0 && !error && (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse space-y-2 border-b border-white/10 px-3 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="h-4 w-3/4 rounded-sm bg-white/8" />
                    <div className="h-4 w-10 shrink-0 rounded-sm bg-white/8" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-32 rounded-sm bg-white/6" />
                    <div className="h-3 w-16 rounded-sm bg-white/6" />
                  </div>
                </div>
              ))
            )}
            {events.map(event => (
              <EventListItem
                key={event.id}
                event={event}
                selected={event.id === selectedId}
                isSignedUp={!!user && event.sign_ups.some(s => s.user_id === user.id)}
                onClick={() => handleSelectEvent(event.id)}
              />
            ))}
          </div>
        </aside>

        {/* Right panel — hidden on mobile when no event is selected */}
        <div className={`${selectedEvent ? 'flex' : 'hidden md:flex'} flex-1 overflow-hidden flex-col`}>
          {!selectedEvent ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="max-w-xs space-y-2 text-center">
                <CalendarDays className="mx-auto h-10 w-10 text-muted-foreground/20" />
                <p className="text-sm text-foreground/80">
                  {loading ? T.events.loadingEvents : T.events.selectEvent}
                </p>
              </div>
            </div>
          ) : viewMode === 'detail' ? (
            <>
              {/* Mobile back button */}
              <div className="shrink-0 border-b border-white/10 px-4 py-2 md:hidden">
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5" onClick={() => setSelectedId(null)}>
                  {T.events.backToEvents}
                </Button>
              </div>
              <EventDetailPanel
                key={selectedEvent.id}
                title={selectedEvent.title}
                startTime={selectedEvent.start_time}
                description={selectedEvent.description}
                eventId={String(selectedEvent.id)}
                signupBase="events"
                signUps={selectedEvent.sign_ups}
                onConfigureSquads={canConfigure ? () => setViewMode('squads') : undefined}
                configLabel={selectedEvent.channel_id === SPECIAL_EVENT_CHANNEL_ID ? 'Configure Event' : 'Configure Squads'}
                onRefresh={() => fetchEvents(true)}
                onSignUpChange={(su) => {
                  if (!user) return
                  setEvents(prev => prev.map(e => {
                    if (String(e.id) !== String(selectedEvent.id)) return e
                    const without = e.sign_ups.filter(s => s.user_id !== user.id)
                    const newSignUps = su ? [...without, su] : without
                    return {
                      ...e,
                      sign_ups: newSignUps,
                      active_count: newSignUps.filter(s => s.is_active && s.class_name !== 'Bench' && s.class_name !== 'Tentative').length,
                    }
                  }))
                }}
              />
            </>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="shrink-0 border-b border-white/10 px-4 py-2">
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1.5" onClick={() => setViewMode('detail')}>
                  {T.events.backToDetails}
                </Button>
              </div>
              <div className="flex-1 overflow-hidden">
                {selectedEvent.channel_id === SPECIAL_EVENT_CHANNEL_ID
                  ? <EventConfigBuilder key={selectedEvent.id} event={selectedEvent} />
                  : <SquadBuilder key={selectedEvent.id} event={selectedEvent} />
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
